import { Link } from "react-router-dom";
import { ArrowRight, ShieldCheck, TrendingUp, Award, Users, HeartHandshake, Sparkles, ClipboardCheck, Star, Quote, CheckCircle2, Stethoscope, Car, Building2, Briefcase, Activity, LineChart } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import LeadForm from "@/components/LeadForm";

const stats = [
  { value: "Since 1987", label: "Family-run firm" },
  { value: "1,000+", label: "Families guided" },
  { value: "5,000+", label: "Policies placed" },
  { value: "₹500 Cr+", label: "Sum assured" },
];

const reasons = [
  { Icon: ShieldCheck, title: "Personalised, unbiased advice", body: "Recommendations based on your goals — not commissions. We compare 30+ insurers and 200+ mutual funds." },
  { Icon: HeartHandshake, title: "Claims-first relationship", body: "We don't disappear after the sale. Our team walks every family through hospital admission, paperwork and settlement." },
  { Icon: Award, title: "Led by a Certified Financial Planner", body: "Founder Vinay Samota is a CFP® with two decades of advisory experience across insurance and investments." },
  { Icon: Users, title: "One point of contact, for life", body: "Renewals, portfolio reviews, claims, top-ups — a single advisor who knows your full picture." },
  { Icon: ClipboardCheck, title: "Transparent comparisons", body: "Plain-English benefit illustrations. Side-by-side comparisons. No fine-print surprises." },
  { Icon: Sparkles, title: "Modern digital + warm human", body: "Online policy issuance, WhatsApp updates, and in-person reviews when you want them." },
];

const services = [
  { Icon: Stethoscope, title: "Health Insurance", body: "Family floaters, senior citizen, top-ups, critical illness.", to: "/services/health" },
  { Icon: ShieldCheck, title: "Term Insurance", body: "Pure protection cover — ₹1 Cr from ~₹700/month.", to: "/services/term" },
  { Icon: Activity, title: "Life Insurance", body: "Endowment, whole-life and guaranteed plans.", to: "/services/life" },
  { Icon: Car, title: "Motor Insurance", body: "Cars, bikes, commercial — cashless repairs nationwide.", to: "/services/motor" },
  { Icon: LineChart, title: "Mutual Funds & SIP", body: "Goal-based investing, annual reviews, tax-efficient.", to: "/services/mutual-funds" },
  { Icon: Building2, title: "Corporate Insurance", body: "Fire, marine, engineering, cyber, liability covers.", to: "/services" },
  { Icon: Briefcase, title: "Employee Benefits", body: "Group health, GPA, GTL for SMEs and startups.", to: "/services" },
  { Icon: TrendingUp, title: "Financial Planning", body: "Retirement, child education, wealth & tax planning.", to: "/services" },
];

const testimonials = [
  {
    name: "Saurabh Marathe",
    role: "Chartered Accountant · Google Local Guide",
    body: "When it comes to insurance I trust Vinay more than I trust myself. Recently he helped me claim reimbursement for my mother's cataract — papers handed over on 20/6, claim received on 27/6. Exceptional knowledge, professionalism, and follow-through.",
    rating: 5,
  },
  {
    name: "Priyanka Kadam",
    role: "IT Developer · Health Insurance + Investments",
    body: "Was hospitalised last month — Vinay walked me through the entire claim intimation process and made it effortless. He's also been guiding my investments for 2 years. Highly knowledgeable and genuinely helpful.",
    rating: 5,
  },
  {
    name: "Tomson Samuel",
    role: "Business Owner, Thane · Car + Health Insurance",
    body: "Samota Insurance plays a major part in my life when it comes to car or health claims — never experienced such a hassle-free settlement before. Mr Vinay is always just a call away. He totally changed my view of insurance.",
    rating: 5,
  },
  {
    name: "Rahul Gharat",
    role: "Chartered Accountant · Google Local Guide",
    body: "Vinay is highly knowledgeable and genuinely takes interest in his clients. Prompt, reliable, and supports you through the entire claims process — from the initial event all the way to final completion. Smooth and stress-free.",
    rating: 5,
  },
  {
    name: "Anita P.",
    role: "Doctor, Thane",
    body: "Vinay sir helped us rebuild our entire protection plan after my husband's surgery. The claim went through smoothly — I can't imagine doing this with anyone else.",
    rating: 5,
  },
  {
    name: "Rohan M.",
    role: "Founder, SaaS startup",
    body: "Got our team's group health, cyber, and D&O sorted in one go. Crystal-clear comparisons, no jargon, no upselling.",
    rating: 5,
  },
];

export default function Home() {
  return (
    <div className="bg-white" data-testid="page-home">
      {/* HERO */}
      <section className="relative isolate overflow-hidden pt-16 sm:pt-20">
        <div className="absolute inset-0 -z-10">
          <img
            src="https://images.pexels.com/photos/3760067/pexels-photo-3760067.jpeg"
            alt="Financial planning consultation"
            className="h-full w-full object-cover"
            loading="eager"
          />
          <div className="absolute inset-0 gradient-hero" />
        </div>

        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-20 sm:py-28 lg:py-36">
          <div className="max-w-2xl text-white animate-fade-up">
            <Badge className="bg-white/15 text-white border-white/20 hover:bg-white/15 font-medium" data-testid="hero-badge">
              <Award className="h-3.5 w-3.5 mr-1.5" /> CFP® Led • Trusted Since 1987
            </Badge>
            <p className="font-display mt-5 text-sm sm:text-base text-emerald-200 italic tracking-wide" data-testid="hero-tagline">
              We insure your happiness.
            </p>
            <h1 className="font-display mt-3 text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight leading-[1.05]">
              Protect Today.<br />
              <span className="text-emerald-300">Prosper Tomorrow.</span>
            </h1>
            <p className="mt-6 text-base sm:text-lg text-white/85 leading-relaxed max-w-xl">
              Insurance, investments and lifelong financial planning — for individuals,
              families and businesses. Transparent advice. Real claims support.
            </p>
            <div className="mt-9 flex flex-col sm:flex-row gap-3">
              <Link to="/contact">
                <Button data-testid="hero-cta-consultation" className="h-12 px-7 bg-emerald-500 hover:bg-emerald-600 text-white rounded-full text-base shadow-lg shadow-emerald-500/30">
                  Get Free Consultation <ArrowRight className="ml-1 h-4 w-4" />
                </Button>
              </Link>
              <Link to="/assessment">
                <Button data-testid="hero-cta-assessment" variant="outline" className="h-12 px-7 bg-white/10 border-white/30 text-white hover:bg-white/20 rounded-full text-base">
                  Review My Insurance
                </Button>
              </Link>
            </div>

            <div className="mt-12 grid grid-cols-2 sm:grid-cols-4 gap-x-6 gap-y-5 max-w-2xl">
              {stats.map((s) => (
                <div key={s.label} data-testid={`hero-stat-${s.label.toLowerCase().replace(/\s+/g, "-")}`}>
                  <div className="font-display text-2xl sm:text-3xl font-semibold text-white">{s.value}</div>
                  <div className="text-xs sm:text-sm text-white/70 mt-1">{s.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* TRUST STRIP */}
      <section className="border-y border-slate-100 bg-slate-50/60">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-6 grid grid-cols-2 md:grid-cols-4 gap-6 text-center md:text-left">
          {[
            "Family firm since 1987",
            "Comprehensive Solutions",
            "Dedicated Claims Team",
            "Transparent & Unbiased",
          ].map((t) => (
            <div key={t} className="flex items-center justify-center md:justify-start gap-2 text-sm font-medium text-slate-700">
              <CheckCircle2 className="h-4 w-4 text-emerald-600" /> {t}
            </div>
          ))}
        </div>
      </section>

      {/* WHY CHOOSE US */}
      <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-20 lg:py-28" data-testid="section-why-choose-us">
        <div className="max-w-2xl">
          <Badge variant="outline" className="border-blue-200 text-blue-900 bg-blue-50/60">Why families & businesses choose us</Badge>
          <h2 className="font-display mt-4 text-3xl sm:text-4xl font-semibold tracking-tight text-slate-900">
            Trust built since <span className="text-gradient-blue">1987</span> — not two clicks.
          </h2>
          <p className="mt-4 text-slate-600 leading-relaxed">
            We're not a call-centre. Every recommendation is reviewed by a CFP® and explained
            in plain language — because financial decisions should be understood, not just signed.
          </p>
        </div>

        <div className="mt-14 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {reasons.map(({ Icon, title, body }) => (
            <div
              key={title}
              data-testid={`reason-${title.toLowerCase().replace(/\s+/g, "-")}`}
              className="group rounded-2xl border border-slate-100 bg-white p-7 soft-shadow hover:-translate-y-1 hover:shadow-xl transition"
            >
              <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-blue-50 text-blue-900 group-hover:bg-blue-900 group-hover:text-white transition">
                <Icon className="h-5 w-5" />
              </span>
              <h3 className="font-display mt-5 text-lg font-semibold text-slate-900">{title}</h3>
              <p className="mt-2 text-sm text-slate-600 leading-relaxed">{body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* SERVICES */}
      <section className="bg-slate-50/60 py-20 lg:py-28" data-testid="section-services">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
            <div className="max-w-2xl">
              <Badge variant="outline" className="border-emerald-200 text-emerald-700 bg-emerald-50/60">Our solutions</Badge>
              <h2 className="font-display mt-4 text-3xl sm:text-4xl font-semibold tracking-tight text-slate-900">
                Everything you need — under one trusted roof.
              </h2>
              <p className="mt-4 text-slate-600 leading-relaxed">
                Protect, save, invest and review — with one advisor who knows your full picture.
              </p>
            </div>
            <Link to="/services">
              <Button variant="outline" className="border-blue-900 text-blue-900 hover:bg-blue-50" data-testid="services-view-all">
                View all services <ArrowRight className="ml-1 h-4 w-4" />
              </Button>
            </Link>
          </div>

          <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {services.map(({ Icon, title, body, to }) => (
              <Link
                key={title}
                to={to}
                data-testid={`service-card-${title.toLowerCase().replace(/\s+/g, "-")}`}
                className="group rounded-2xl bg-white p-6 border border-slate-100 hover:border-blue-200 hover:-translate-y-1 transition soft-shadow"
              >
                <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-blue-50 text-blue-900 group-hover:bg-blue-900 group-hover:text-white transition">
                  <Icon className="h-5 w-5" />
                </span>
                <h3 className="font-display mt-5 text-base font-semibold text-slate-900">{title}</h3>
                <p className="mt-1.5 text-sm text-slate-600 leading-relaxed">{body}</p>
                <span className="inline-flex items-center gap-1 mt-4 text-sm font-medium text-blue-900 group-hover:text-emerald-600 transition">
                  Learn more <ArrowRight className="h-3.5 w-3.5" />
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ASSESSMENT PROMO */}
      <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-20 lg:py-28" data-testid="section-assessment-promo">
        <div className="relative overflow-hidden rounded-3xl gradient-blue p-10 sm:p-14 lg:p-16 text-white">
          <div className="absolute -right-20 -top-20 h-72 w-72 rounded-full bg-emerald-400/20 blur-3xl" />
          <div className="absolute -left-16 -bottom-16 h-64 w-64 rounded-full bg-white/10 blur-3xl" />
          <div className="relative grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7">
              <Badge className="bg-white/15 text-white border-white/20 hover:bg-white/15">
                <Sparkles className="h-3.5 w-3.5 mr-1.5" /> Free • 3 minutes
              </Badge>
              <h2 className="font-display mt-5 text-3xl sm:text-4xl font-semibold tracking-tight">
                Get your personalised<br /> Financial Health Score.
              </h2>
              <p className="mt-5 text-white/85 leading-relaxed max-w-xl">
                Answer 8 quick questions. We'll compute a 0-100 score and recommend
                exactly which insurance & investment products you actually need — no upsell,
                no spam, no obligation.
              </p>
              <Link to="/assessment">
                <Button data-testid="assessment-promo-cta" className="mt-8 h-12 px-7 bg-emerald-500 hover:bg-emerald-600 text-white rounded-full text-base">
                  Start free assessment <ArrowRight className="ml-1 h-4 w-4" />
                </Button>
              </Link>
            </div>
            <div className="lg:col-span-5">
              <div className="rounded-2xl bg-white/10 border border-white/15 p-6 backdrop-blur-sm">
                {[
                  "Sized term & health cover for your family",
                  "Goal-based SIP recommendations",
                  "Identifies gaps in existing policies",
                  "Downloadable summary you can keep",
                ].map((b) => (
                  <div key={b} className="flex items-start gap-3 py-2.5">
                    <CheckCircle2 className="h-5 w-5 text-emerald-300 mt-0.5 shrink-0" />
                    <span className="text-sm text-white/95">{b}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="bg-slate-50/60 py-16 lg:py-20" data-testid="section-testimonials">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl">
            <Badge variant="outline" className="border-amber-200 text-amber-700 bg-amber-50/60">
              <Star className="h-3 w-3 mr-1 fill-current" /> 4.9 / 5 on Google
            </Badge>
            <h2 className="font-display mt-4 text-3xl sm:text-4xl font-semibold tracking-tight text-slate-900">
              The most reliable financial relationship in our clients' lives.
            </h2>
          </div>
          <div className="mt-12 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {testimonials.slice(0, 6).map((t) => (
              <div key={t.name} data-testid={`testimonial-${t.name.toLowerCase().replace(/\s+/g, "-")}`} className="rounded-2xl bg-white p-7 border border-slate-100 soft-shadow">
                <Quote className="h-7 w-7 text-blue-900/20" />
                <p className="mt-4 text-sm text-slate-700 leading-relaxed">"{t.body}"</p>
                <div className="mt-5 flex items-center justify-between">
                  <div>
                    <div className="text-sm font-semibold text-slate-900">{t.name}</div>
                    <div className="text-xs text-slate-500">{t.role}</div>
                  </div>
                  <div className="flex gap-0.5">
                    {Array.from({ length: t.rating }).map((_, i) => (
                      <Star key={i} className="h-3.5 w-3.5 fill-amber-400 text-amber-400" />
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CONTACT CTA */}
      <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-12 lg:py-16" data-testid="section-contact-cta">
        <div className="grid gap-12 lg:grid-cols-2 items-center">
          <div>
            <Badge variant="outline" className="border-blue-200 text-blue-900 bg-blue-50/60">Schedule your review</Badge>
            <h2 className="font-display mt-4 text-3xl sm:text-4xl font-semibold tracking-tight text-slate-900">
              Spend 20 minutes with us. <span className="text-gradient-blue">Walk away with clarity.</span>
            </h2>
            <p className="mt-4 text-slate-600 leading-relaxed max-w-md">
              A complimentary financial review — over a call, video meeting, or at our Thane office.
              Bring whatever policies you have. We'll point out gaps and savings.
            </p>
            <ul className="mt-6 space-y-2.5 text-sm text-slate-700">
              {[
                "100% obligation-free conversation",
                "No product pitched in the first call",
                "Get a written summary within 48 hours",
              ].map((b) => (
                <li key={b} className="flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4 text-emerald-600" /> {b}
                </li>
              ))}
            </ul>
          </div>
          <LeadForm source="home_bottom" testIdPrefix="home" />
        </div>
      </section>
    </div>
  );
}
